import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoginPermissionsDto } from '../../models/user-management/auth.models';
import { ModuleIds, ModuleKey } from '../../constants/module-ids';

@Injectable({ providedIn: 'root' })
export class AuthzService {
  /** ACTION -> bit (e.g., VIEW -> 1, EDIT -> 4, ...) */
  private actionMap = new Map<string, number>();
  /** moduleId -> mask (bitwise OR of granted actions) */
  private maskByModuleId = new Map<number, number>();

  private loadedSubject = new BehaviorSubject<boolean>(false);
  readonly loaded$ = this.loadedSubject.asObservable();

  // -------------------- Key Management --------------------

  /**
   * Build a unique localStorage key per user.
   * Format: permissions.v1::{companyId}::{companyAdminId}::{employerTypeId}
   */
  private buildKey(
    companyId: string | number,
    companyAdminId: string | number,
    employerTypeId: string | number
  ): string {
    return `permissions.v1::${companyId}::${companyAdminId}::${employerTypeId}`;
  }

  /**
   * Reads the current session identifiers from sessionStorage.
   * Returns null if any required identifier is missing.
   */
  private getCurrentKeyParams(): {
    companyId: string;
    companyAdminId: string;
    employerTypeId: string;
  } | null {
    const companyId = sessionStorage.getItem('companyId');
    const companyAdminId = sessionStorage.getItem('companyAdminId');
    const employerTypeId = sessionStorage.getItem('employerTypeId');

    if (!companyId || !companyAdminId || !employerTypeId) return null;

    return { companyId, companyAdminId, employerTypeId };
  }

  /**
   * Returns the resolved localStorage key for the current session.
   * Returns null if session identifiers are not yet stored.
   */
  getCurrentKey(): string | null {
    const params = this.getCurrentKeyParams();
    if (!params) return null;
    return this.buildKey(params.companyId, params.companyAdminId, params.employerTypeId);
  }

  // -------------------- Lifecycle --------------------

  load(perms: LoginPermissionsDto | null | undefined): void {
    this.actionMap.clear();
    this.maskByModuleId.clear();

    if (perms) {
      Object.entries(perms.actionMap ?? {}).forEach(([name, bit]) =>
        this.actionMap.set(name.toUpperCase(), bit)
      );
      (perms.modules ?? []).forEach(m =>
        this.maskByModuleId.set(m.moduleId, m.mask)
      );
    }

    this.loadedSubject.next(true);
  }

  /**
   * Persist permissions using the unique key.
   * Must be called AFTER sessionStorage has companyId, companyAdminId, employerTypeId.
   */
  saveToStorage(
    perms: LoginPermissionsDto | null | undefined,
    companyId: string | number,
    companyAdminId: string | number,
    employerTypeId: string | number
  ): void {
    if (!perms) return;
    const key = this.buildKey(companyId, companyAdminId, employerTypeId);
    localStorage.setItem(key, JSON.stringify(perms));
  }

  /**
   * Restore permissions on app init using the current session identifiers.
   * Returns false if session identifiers are missing (user not logged in).
   */
  restoreFromStorage(): boolean {
    const key = this.getCurrentKey();
    if (!key) {
      this.load(null);
      return false;
    }

    const raw = localStorage.getItem(key);
    if (!raw) {
      this.load(null);
      return false;
    }

    try {
      const perms = JSON.parse(raw) as LoginPermissionsDto;
      this.load(perms);
      return true;
    } catch {
      this.load(null);
      return false;
    }
  }

  /**
   * Checks whether the permissions entry exists in localStorage
   * for the current session user.
   * 
   * Call this in app.ts on every refresh.
   * If returns false → call your logout function.
   */
  hasValidPermissionsInStorage(): boolean {
    const key = this.getCurrentKey();
    if (!key) return false;
    return localStorage.getItem(key) !== null;
  }

  /**
   * Clears ONLY the current user's permissions from localStorage.
   * Safe for multi-user scenarios — does not wipe other users' entries.
   */
  clear(): void {
    const key = this.getCurrentKey();
    if (key) localStorage.removeItem(key);

    this.actionMap.clear();
    this.maskByModuleId.clear();
    this.loadedSubject.next(false);
  }

  // -------------------- Queries --------------------

  canModule(module: number | ModuleKey, action: string): boolean {
    const id = this.asModuleId(module);
    const bit = this.getActionBit(action);
    if (!bit) return false;
    const mask = this.maskByModuleId.get(id) ?? 0;
    return (mask & bit) !== 0;
  }

  canAnyModule(module: number | ModuleKey, actions: string[]): boolean {
    const id = this.asModuleId(module);
    const mask = this.maskByModuleId.get(id) ?? 0;
    let orBits = 0;
    for (const a of actions) orBits |= this.getActionBit(a);
    return (mask & orBits) !== 0;
  }

  allowedActionsForModule(module: number | ModuleKey): string[] {
    const id = this.asModuleId(module);
    const mask = this.maskByModuleId.get(id) ?? 0;
    const out: string[] = [];
    this.actionMap.forEach((bit, name) => {
      if ((mask & bit) !== 0) out.push(name);
    });
    return out;
  }

  getMask(module: number | ModuleKey): number {
    const id = this.asModuleId(module);
    return this.maskByModuleId.get(id) ?? 0;
  }

  getActionBit(action: string): number {
    return this.actionMap.get((action || '').toUpperCase()) ?? 0;
  }

  isLoaded(): boolean {
    return this.loadedSubject.value;
  }

  private asModuleId(module: number | ModuleKey): number {
    return typeof module === 'number' ? module : ModuleIds[module];
  }
}